import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ImportCustomerContactPersonsResponse,
  ImportCustomerNotesResponse,
  ImportCustomersResponse,
} from "./customersApi";
import { importCustomerContactPersons, importCustomerNotes, importCustomers, listCustomers } from "./customersApi";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import { useMemo } from "react";

export const useCustomers = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: ({ signal }) => listCustomers(signal),
  });

  const importCustomersMutation = useMutation<ImportCustomersResponse, unknown, FormData>({
    mutationFn: (formData) => importCustomers(formData),
    onSuccess: async ({ addedCustomers, updatedCustomers }) => {
      enqueueSnackbar(`Klienci zostali zaimportowani. Nowi: ${addedCustomers}, zmodyfikowani: ${updatedCustomers}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey.at(0) === "customers" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaimportować klientów. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const importCustomerContactPersonsMutation = useMutation<ImportCustomerContactPersonsResponse, unknown, FormData>({
    mutationFn: (formData) => importCustomerContactPersons(formData),
    onSuccess: async ({ addedPersons, affectedCustomers }) => {
      enqueueSnackbar(
        `Osoby kontaktowe zostały zaimportowane. Dodano ${addedPersons} osób dla ${affectedCustomers} klientów`,
        {
          variant: "success",
        },
      );
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey.at(0) === "customers" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaimportować osób kontaktowych. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const importCustomerNotesMutation = useMutation<ImportCustomerNotesResponse, unknown, FormData>({
    mutationFn: (formData) => importCustomerNotes(formData),
    onSuccess: async ({ addedNotes, affectedCustomers }) => {
      enqueueSnackbar(`Notatki zostały zaimportowane. Dodano ${addedNotes} notatek dla ${affectedCustomers} klientów`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey.at(0) === "customers" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaimportować notatek. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const customers = useMemo(() => (data ? sortBy(data, (c) => c.name.toLowerCase()) : []), [data]);
  return {
    customers,
    importCustomers: (formData: FormData) => importCustomersMutation.mutateAsync(formData),
    importCustomerContactPersons: (formData: FormData) => importCustomerContactPersonsMutation.mutateAsync(formData),
    importCustomerNotes: (formData: FormData) => importCustomerNotesMutation.mutateAsync(formData),
    isLoading,
  };
};
